import { useState, useEffect, useRef } from 'react';
import { formatTime } from '../../utils/formatters';
import type { VideoClip } from '../../types';
import { Modal, Button } from '../ui';

export interface VideoTrimmerProps {
    clip: VideoClip | null;
    isOpen: boolean;
    onClose: () => void;
    onApplyTrim: (clipId: string, startTime: number, endTime: number) => void;
}

export function VideoTrimmer({ clip, isOpen, onClose, onApplyTrim }: VideoTrimmerProps) {
    const [startPercent, setStartPercent] = useState(0);
    const [endPercent, setEndPercent] = useState(100);
    const [videoDuration, setVideoDuration] = useState(0);

    const videoRef = useRef<HTMLVideoElement>(null);

    // Reset when clip changes
    useEffect(() => {
        if (clip) {
            const startPct = (clip.startTime / clip.metadata.duration) * 100;
            const endPct = (clip.endTime / clip.metadata.duration) * 100;
            setStartPercent(startPct);
            setEndPercent(endPct);
        }
    }, [clip]);

    // Update video duration when metadata loads
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleLoadedMetadata = () => {
            setVideoDuration(video.duration);
        };

        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        return () => video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    }, []);

    const handleStartChange = (value: number) => {
        const newStart = Math.min(value, endPercent - 1);
        setStartPercent(newStart);
        updateVideoPreview(newStart);
    };

    const handleEndChange = (value: number) => {
        const newEnd = Math.max(value, startPercent + 1);
        setEndPercent(newEnd);
    };

    const updateVideoPreview = (percent: number) => {
        const video = videoRef.current;
        if (video && videoDuration > 0) {
            video.currentTime = (percent / 100) * videoDuration;
        }
    };

    const handleApply = () => {
        if (!clip) return;

        const startTime = (startPercent / 100) * clip.metadata.duration;
        const endTime = (endPercent / 100) * clip.metadata.duration;

        onApplyTrim(clip.id, startTime, endTime);
        onClose();
    };

    const handleCancel = () => {
        onClose();
    };

    if (!clip) return null;

    const startTime = (startPercent / 100) * clip.metadata.duration;
    const endTime = (endPercent / 100) * clip.metadata.duration;
    const duration = endTime - startTime;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Trim Video Klip" size="lg">
            <div className="space-y-6">
                {/* Video Preview */}
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                    <video
                        ref={videoRef}
                        src={URL.createObjectURL(clip.file)}
                        className="w-full h-full"
                        controls
                    />
                </div>

                {/* Time Display */}
                <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                        <span className="text-gray-400">Start:</span>
                        <span className="font-mono font-medium">{formatTime(startTime)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-gray-400">Slut:</span>
                        <span className="font-mono font-medium">{formatTime(endTime)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-gray-400">Varighed:</span>
                        <span className="font-mono font-medium text-primary-400">{formatTime(duration)}</span>
                    </div>
                </div>

                {/* Range Sliders */}
                <div className="relative h-12">
                    {/* Track */}
                    <div className="absolute top-1/2 -translate-y-1/2 w-full h-2 bg-gray-700 rounded-full" />

                    {/* Selected Range */}
                    <div
                        className="absolute top-1/2 -translate-y-1/2 h-2 bg-primary-600 rounded-full"
                        style={{
                            left: `${startPercent}%`,
                            width: `${endPercent - startPercent}%`,
                        }}
                    />

                    {/* Start Slider */}
                    <input
                        type="range"
                        min="0"
                        max="100"
                        step="0.1"
                        value={startPercent}
                        onChange={(e) => handleStartChange(parseFloat(e.target.value))}
                        className="absolute top-0 w-full h-12 opacity-0 cursor-pointer z-10"
                        style={{ pointerEvents: 'auto' }}
                    />

                    {/* End Slider */}
                    <input
                        type="range"
                        min="0"
                        max="100"
                        step="0.1"
                        value={endPercent}
                        onChange={(e) => handleEndChange(parseFloat(e.target.value))}
                        className="absolute top-0 w-full h-12 opacity-0 cursor-pointer z-10"
                        style={{ pointerEvents: 'auto' }}
                    />

                    {/* Start Handle */}
                    <div
                        className="absolute top-1/2 -translate-y-1/2 w-4 h-8 bg-white rounded shadow-lg pointer-events-none z-20"
                        style={{ left: `calc(${startPercent}% - 8px)` }}
                    />

                    {/* End Handle */}
                    <div
                        className="absolute top-1/2 -translate-y-1/2 w-4 h-8 bg-white rounded shadow-lg pointer-events-none z-20"
                        style={{ left: `calc(${endPercent}% - 8px)` }}
                    />
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-700">
                    <Button variant="secondary" onClick={handleCancel}>
                        Annuller
                    </Button>
                    <Button variant="primary" onClick={handleApply}>
                        Anvend Trim
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
